import { useEffect, useState, type ReactNode } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router";
import type { UserRole } from "../../api/types";
import { useSession } from "../../lib/session";

type NavItem = { to: string; label: string; short: string; roles: UserRole[]; icon: string };

const ALL: UserRole[] = ["SuperAdmin", "GigAdmin", "FinanceAdmin"];

const NAV: NavItem[] = [
  {
    to: "/admin/gig",
    label: "Gig readiness",
    short: "Gig",
    roles: ["SuperAdmin", "GigAdmin"],
    // bar chart
    icon: "M4 20V10m6 10V4m6 16v-6m4 6H2",
  },
  {
    to: "/admin/finance",
    label: "Access to Finance",
    short: "Finance",
    roles: ["SuperAdmin", "FinanceAdmin"],
    // bank
    icon: "M3 10h18M5 10v8m4-8v8m6-8v8m4-8v8M3 21h18M12 3l9 5H3l9-5z",
  },
  {
    to: "/admin/activity",
    label: "Activity",
    short: "Activity",
    roles: ALL,
    // pulse
    icon: "M3 12h4l3-8 4 16 3-8h4",
  },
  {
    to: "/admin/platforms",
    label: "Partner platforms",
    short: "Platforms",
    roles: ["SuperAdmin", "GigAdmin"],
    // globe
    icon: "M12 21a9 9 0 100-18 9 9 0 000 18zM3 12h18M12 3c2.5 2.7 3.8 5.7 3.8 9s-1.3 6.3-3.8 9c-2.5-2.7-3.8-5.7-3.8-9s1.3-6.3 3.8-9z",
  },
  {
    to: "/admin/roster",
    label: "Participant roster",
    short: "Roster",
    roles: ["SuperAdmin", "GigAdmin"],
    // people
    icon: "M9 11a4 4 0 100-8 4 4 0 000 8zM1 21a8 8 0 0116 0M16 3.5a4 4 0 010 7.5M23 21a8 8 0 00-5-7.4",
  },
  {
    to: "/admin/team",
    label: "Admin team",
    short: "Team",
    roles: ALL,
    // shield
    icon: "M12 3l8 3v6c0 4.5-3.4 8.3-8 9-4.6-.7-8-4.5-8-9V6l8-3zM9 12l2 2 4-4",
  },
];

const MORE_ICON = "M5 12h.01M12 12h.01M19 12h.01";
const ACCOUNT_ICON = "M12 12a4 4 0 100-8 4 4 0 000 8zM4 21a8 8 0 0116 0";

/* A phone tab bar stays readable up to five tabs; past that the rest go under "More". */
const MAX_TABS = 5;

const SIGN_OUT_ICON = "M15 17l5-5-5-5M20 12H9M12 21H5a2 2 0 01-2-2V5a2 2 0 012-2h7";

const ROLE_LABEL: Record<UserRole, string> = {
  SuperAdmin: "Super admin",
  GigAdmin: "Gig team",
  FinanceAdmin: "Finance team",
  Participant: "Participant",
};

function Icon({ d, size = 18 }: { d: string; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className="shrink-0"
    >
      <path
        d={d}
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function Initials({ first, last, email }: { first: string; last: string; email: string }) {
  return (
    <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent-tint text-[13px] font-bold text-accent">
      {(first[0] ?? email[0] ?? "?").toUpperCase()}
      {(last[0] ?? "").toUpperCase()}
    </span>
  );
}

/**
 * The admin workspace: a wide layout with a sidebar, unlike the participant app's single phone-first column.
 * Admins scan dozens of rows at a desk; the participant chrome (logo band, centred 820px) wasted that space.
 *
 * On a phone it behaves like a native app: the top bar holds only who is signed in and Sign out, and the
 * sections move to a bottom tab bar (icon + short label) within thumb reach.
 */
export function AdminShell({
  title,
  actions,
  children,
}: {
  title: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  const { user, signOut } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  useEffect(() => setMoreOpen(false), [location.pathname]);

  // Until a temporary password is replaced, the only page open is My account, so offer nothing else.
  const items = NAV.filter(
    (item) => user && !user.mustChangePassword && item.roles.includes(user.role),
  );
  const tabs = items.length > MAX_TABS ? items.slice(0, MAX_TABS - 1) : items;
  const overflow = items.length > MAX_TABS ? items.slice(MAX_TABS - 1) : [];
  const moreActive = overflow.some((item) => location.pathname.startsWith(item.to));
  const name = user ? `${user.firstName} ${user.lastName}`.trim() || user.email : "";

  const signOutAndLeave = () => {
    signOut();
    void navigate("/admin", { replace: true });
  };

  return (
    <div className="flex min-h-dvh bg-[#f7f2ea] text-text">
      <aside className="sticky top-0 hidden h-dvh w-[248px] shrink-0 flex-col border-r border-border bg-surface md:flex print:hidden">
        <div className="border-b border-border px-5 py-5">
          <img
            src="/icons/gateway-mastercard-logo.png"
            alt="Mastercard Foundation GATEWAY"
            className="h-9 w-auto"
          />
          <div className="mt-3 font-[family-name:var(--font-display)] text-[15px] font-extrabold text-accent">
            Readiness Engine
          </div>
          <div className="text-[10.5px] font-semibold uppercase tracking-[0.08em] text-text-muted">
            Admin workspace
          </div>
        </div>
        <nav aria-label="Admin" className="flex flex-1 flex-col gap-1 p-3">
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-[10px] px-3 py-2.5 text-[14px] font-semibold transition-colors ${
                  isActive
                    ? "bg-accent text-on-accent"
                    : "text-text-muted hover:bg-surface-high hover:text-text"
                }`
              }
            >
              <Icon d={item.icon} />
              {item.label}
            </NavLink>
          ))}
        </nav>
        {user ? (
          <div className="border-t border-border p-4">
            <Link
              to="/admin/account"
              aria-label="My account"
              className="-m-2 flex items-center gap-3 rounded-[10px] p-2 hover:bg-surface-high"
            >
              <Initials first={user.firstName} last={user.lastName} email={user.email} />
              <div className="min-w-0 flex-1">
                <div className="truncate text-[13.5px] font-bold">{name}</div>
                <div className="truncate text-[11.5px] text-text-muted">
                  {ROLE_LABEL[user.role]} · My account
                </div>
              </div>
            </Link>
            <button
              type="button"
              onClick={signOutAndLeave}
              className="mt-3 w-full rounded-[9px] border border-border py-2 text-[13px] font-semibold text-text-muted hover:border-accent hover:text-accent"
            >
              Sign out
            </button>
          </div>
        ) : null}
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {user ? (
          <div className="sticky top-0 z-30 flex items-center gap-3 border-b border-border bg-surface/95 px-4 py-2.5 backdrop-blur md:hidden print:hidden">
            <Link
              to="/admin/account"
              aria-label="My account"
              className="flex min-w-0 flex-1 items-center gap-3 rounded-[10px] active:bg-surface-high"
            >
              <Initials first={user.firstName} last={user.lastName} email={user.email} />
              <div className="min-w-0 flex-1">
                <div className="truncate text-[14px] font-bold">{name}</div>
                <div className="truncate text-[11.5px] text-text-muted">
                  {ROLE_LABEL[user.role]}
                </div>
              </div>
            </Link>
            <button
              type="button"
              onClick={signOutAndLeave}
              className="inline-flex min-h-10 items-center gap-1.5 rounded-[10px] border border-border px-3 text-[13px] font-semibold text-text-muted active:bg-surface-high"
            >
              <Icon d={SIGN_OUT_ICON} size={16} />
              Sign out
            </button>
          </div>
        ) : null}
        <header className="flex flex-wrap items-center justify-between gap-3 px-4 pb-2 pt-5 sm:px-8 md:pt-6">
          <h1 className="text-[22px] text-text md:text-[24px]">{title}</h1>
          {actions ? <div className="flex flex-wrap gap-2">{actions}</div> : null}
        </header>
        {/* On phones the extra bottom padding keeps the last rows clear of the tab bar. */}
        <main className="min-w-0 flex-1 px-4 pb-[calc(env(safe-area-inset-bottom)+92px)] sm:px-8 md:pb-12">
          {children}
        </main>
      </div>

      <nav
        aria-label="Admin sections"
        className="fixed inset-x-0 bottom-0 z-30 flex border-t border-border bg-surface/95 pb-[env(safe-area-inset-bottom)] shadow-[0_-4px_16px_rgba(35,20,22,0.06)] backdrop-blur md:hidden print:hidden"
      >
        {tabs.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            aria-label={item.label}
            className={({ isActive }) =>
              `flex min-h-[62px] flex-1 flex-col items-center justify-center gap-0.5 text-[11px] font-semibold transition-colors ${
                isActive ? "text-accent" : "text-text-muted active:text-text"
              }`
            }
          >
            {({ isActive }) => (
              <>
                <span
                  className={`inline-flex h-8 w-14 items-center justify-center rounded-full transition-colors ${
                    isActive ? "bg-accent-tint" : ""
                  }`}
                >
                  <Icon d={item.icon} size={22} />
                </span>
                <span aria-hidden="true">{item.short}</span>
              </>
            )}
          </NavLink>
        ))}
        {overflow.length ? (
          <button
            type="button"
            aria-label="More sections"
            aria-expanded={moreOpen}
            onClick={() => setMoreOpen(!moreOpen)}
            className={`flex min-h-[62px] flex-1 flex-col items-center justify-center gap-0.5 text-[11px] font-semibold ${
              moreActive || moreOpen ? "text-accent" : "text-text-muted"
            }`}
          >
            <span
              className={`inline-flex h-8 w-14 items-center justify-center rounded-full ${
                moreActive ? "bg-accent-tint" : ""
              }`}
            >
              <Icon d={MORE_ICON} size={24} />
            </span>
            <span aria-hidden="true">More</span>
          </button>
        ) : null}
      </nav>

      {moreOpen ? (
        <>
          <div
            className="fixed inset-0 z-20 bg-[#231416]/25 md:hidden"
            aria-hidden="true"
            onClick={() => setMoreOpen(false)}
          />
          <nav
            aria-label="More sections"
            className="fixed inset-x-3 bottom-[calc(env(safe-area-inset-bottom)+72px)] z-30 rounded-[16px] border border-border bg-surface p-2 shadow-[0_12px_40px_rgba(35,20,22,0.18)] md:hidden"
          >
            {[...overflow, { to: "/admin/account", label: "My account", icon: ACCOUNT_ICON }].map(
              (item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    `flex min-h-12 items-center gap-3 rounded-[10px] px-3 text-[14.5px] font-semibold ${
                      isActive ? "bg-accent-tint text-accent" : "text-text active:bg-surface-high"
                    }`
                  }
                >
                  <Icon d={item.icon} size={20} />
                  {item.label}
                </NavLink>
              ),
            )}
          </nav>
        </>
      ) : null}
    </div>
  );
}
